import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { BellRing, Download, Plus, Printer, QrCode, RefreshCw, Trash2, Users, UtensilsCrossed, XCircle } from 'lucide-react';
import type { Permission } from '@garlic/shared';
import { ApiError, api, errorMessage, fieldErrors, patch, post } from '../../lib/api';
import { formatBRL, formatDateTime, relativeTime } from '../../lib/format';
import { Alert, Badge, Button, Card, EmptyState, Field, Input, PageHeader, Spinner, cx } from '../../ui/primitives';
import { Dialog, useToast } from '../../ui/feedback';
import { Sheet } from '../../ui/sheet';
import { OrderStatusBadge } from '../../menu/OrderStatus';
import type { OrderView } from '../../menu/types';
import type { TenantRestaurant } from '../TenantApp';
import { staffPath } from '../../lib/paths';
import { CancelDialog, canCancelOrder, type CancelTarget } from './CancelDialog';

export type TableRow = {
  id: string; label: string; status: 'ACTIVE' | 'INACTIVE'; qrUrl: string;
  session: null | {
    id: string; number: number; openedAt: string; billRequestedAt: string | null; participants: number; orders: number; consumedCents: number; itemCount: number;
    paidCents: number; dueCents: number; processingCents: number; settled: boolean;
  };
};

export function useTables() {
  return useQuery({ queryKey: ['staff-tables'], queryFn: () => api<TableRow[]>('/api/staff/tables'), refetchInterval: 15_000 });
}

export function StaffTables({ restaurant, permissions }: { restaurant: TenantRestaurant; permissions: Permission[] }) {
  const can = (p: Permission) => permissions.includes(p);
  const q = useTables();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const tables = q.data ?? [];
  const busy = tables.filter((t) => t.session).length;
  const billRequests = tables.filter((t) => t.session?.billRequestedAt).length;

  return (
    <>
      <PageHeader
        title="Mesas e QR Codes"
        description={q.data ? `${busy} ocupada(s) · ${tables.length - busy} livre(s)${billRequests ? ` · ${billRequests} pedindo a conta` : ''}` : undefined}
        action={
          <div className="flex flex-wrap gap-2">
            {tables.length > 0 && (
              <Button variant="secondary" icon={<Printer className="size-4" />} onClick={() => navigate(staffPath('/mesas/imprimir'))}>Imprimir QR Codes</Button>
            )}
            {can('tables.manage') && <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>Adicionar mesas</Button>}
          </div>
        }
      />
      {q.isPending && <Spinner />}
      {q.isError && <Alert tone="danger">{errorMessage(q.error)}</Alert>}
      {q.data && tables.length === 0 && (
        <Card>
          <EmptyState icon={<QrCode className="size-5" />} title="Nenhuma mesa cadastrada" action={can('tables.manage') && <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>Adicionar mesas</Button>}>
            Cada mesa ganha um QR Code próprio. O cliente escaneia, pede e acompanha pelo celular.
          </EmptyState>
        </Card>
      )}
      {tables.length > 0 && (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {tables.map((t) => (
            <li key={t.id}>
              <button
                onClick={() => setOpenId(t.id)}
                className={cx(
                  'flex h-full w-full flex-col rounded-2xl border p-4 text-left shadow-card transition-colors',
                  t.status === 'INACTIVE' ? 'border-dashed border-line bg-paper text-ink-3' : t.session?.billRequestedAt ? 'border-warn-600/40 bg-warn-50' : t.session ? 'border-garlic-200 bg-garlic-50' : 'border-line bg-white hover:bg-paper',
                )}
              >
                <span className="flex items-start justify-between gap-2">
                  <span className="font-display text-2xl font-semibold">{t.label}</span>
                  {t.session?.billRequestedAt && <BellRing className="size-4 text-warn-600" aria-label="Pediu a conta" />}
                </span>
                <span className="mt-1 text-xs">
                  {t.status === 'INACTIVE' ? 'Desativada' : t.session ? `Comanda ${t.session.number} · ${relativeTime(t.session.openedAt)}` : 'Livre'}
                </span>
                {t.session && (
                  <span className="mt-2 flex items-center justify-between text-xs text-ink-2">
                    <span className="inline-flex items-center gap-1"><Users className="size-3" /> {t.session.participants}</span>
                    {can('accounts.view') && <span className="font-semibold tabular-nums">{formatBRL(t.session.consumedCents)}</span>}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {creating && <CreateTablesDialog onClose={() => setCreating(false)} />}
      <TableSheet table={tables.find((t) => t.id === openId) ?? null} restaurant={restaurant} permissions={permissions} onClose={() => setOpenId(null)} />
    </>
  );
}

function CreateTablesDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [mode, setMode] = useState<'batch' | 'single'>('batch');
  const [f, setF] = useState({ label: '', prefix: '', from: '1', to: '10' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      if (mode === 'single') {
        await post('/api/staff/tables', { label: f.label });
        toast.success(`Mesa ${f.label} criada`);
      } else {
        const r = await post<{ created: number; skipped: number }>('/api/staff/tables/batch', { prefix: f.prefix, from: Number(f.from), to: Number(f.to) });
        toast.success(`${r.created} mesa(s) criada(s)${r.skipped ? `, ${r.skipped} já existiam` : ''}`);
      }
      await qc.invalidateQueries({ queryKey: ['staff-tables'] });
      onClose();
    } catch (err) {
      setErrors(fieldErrors(err));
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} title="Adicionar mesas" footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button type="submit" form="tables-form" loading={busy}>Criar</Button></>}>
      <form id="tables-form" onSubmit={submit} className="space-y-4" noValidate>
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-paper-2 p-1 text-sm">
          {([['batch', 'Várias de uma vez'], ['single', 'Uma mesa']] as const).map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMode(k)} className={cx('rounded-lg py-1.5 font-medium', mode === k ? 'bg-white shadow-sm' : 'text-ink-3')}>{l}</button>
          ))}
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
        {mode === 'single' ? (
          <Field label="Número ou nome" error={errors.label}>{(id) => <Input id={id} autoFocus placeholder="Ex.: 12, Varanda 3, Balcão" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />}</Field>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Prefixo (opcional)">{(id) => <Input id={id} placeholder="Ex.: V" value={f.prefix} onChange={(e) => setF({ ...f, prefix: e.target.value })} />}</Field>
              <Field label="De">{(id) => <Input id={id} type="number" min={1} value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />}</Field>
              <Field label="Até" error={errors.to}>{(id) => <Input id={id} type="number" min={1} value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} />}</Field>
            </div>
            <p className="text-xs text-ink-3">Serão criadas as mesas {f.prefix}{f.from} a {f.prefix}{f.to}. Mesas que já existem são puladas.</p>
          </>
        )}
      </form>
    </Dialog>
  );
}

type SessionDetail = {
  id: string; number: number; status: string; openedAt: string; billRequestedAt: string | null;
  orders: (OrderView & { source: string })[];
  totals: { consumedCents: number; itemCount: number } | null;
  _count: { participants: number };
};

function TableSheet({ table, restaurant, permissions, onClose }: { table: TableRow | null; restaurant: TenantRestaurant; permissions: Permission[]; onClose: () => void }) {
  const can = (p: Permission) => permissions.includes(p);
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [qr, setQr] = useState<string>();
  const [closing, setClosing] = useState(false);
  const [cancelling, setCancelling] = useState<CancelTarget | null>(null);
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState('');
  const sessionId = table?.session?.id;
  const detail = useQuery({
    queryKey: ['staff-session', sessionId],
    queryFn: () => api<SessionDetail>(`/api/staff/sessions/${sessionId}`),
    enabled: !!sessionId,
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (!table) return;
    setLabel(table.label);
    setEditing(false);
    void QRCode.toDataURL(table.qrUrl, { margin: 1, width: 480, errorCorrectionLevel: 'M' }).then(setQr);
  }, [table?.id, table?.qrUrl, table]);

  if (!table) return <Sheet open={false} onClose={onClose} title="">{null}</Sheet>;

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['staff-tables'] });
    await qc.invalidateQueries({ queryKey: ['staff-session'] });
  };
  const run = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      await refresh();
      toast.success(msg);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={
        <div>
          <h2 className="font-display text-xl font-semibold">Mesa {table.label}</h2>
          <p className="text-sm text-ink-3">{table.session ? `Comanda ${table.session.number} aberta ${relativeTime(table.session.openedAt)}` : table.status === 'INACTIVE' ? 'Desativada' : 'Livre'}</p>
        </div>
      }
    >
      <div className="space-y-6">
        {table.session ? (
          <section>
            {table.session.billRequestedAt && <div className="mb-3"><Alert tone="warning" title="Cliente pediu a conta">{formatDateTime(table.session.billRequestedAt, restaurant.timezone)}</Alert></div>}
            <div className="flex flex-wrap gap-2">
              {can('orders.create') && <Button icon={<UtensilsCrossed className="size-4" />} onClick={() => navigate(staffPath(`/mesas/${table.id}/pedido`))}>Lançar pedido</Button>}
              {can('sessions.close') && <Button variant="secondary" icon={<XCircle className="size-4" />} onClick={() => setClosing(true)}>Encerrar comanda</Button>}
            </div>
            {detail.isPending ? (
              <Spinner />
            ) : detail.data ? (
              <>
                {detail.data.totals && (
                  <div className="mt-4 flex items-baseline justify-between rounded-xl bg-paper-2 px-4 py-3">
                    <span className="text-sm text-ink-3">Consumo ({detail.data.totals.itemCount} itens)</span>
                    <span className="text-lg font-semibold tabular-nums">{formatBRL(detail.data.totals.consumedCents)}</span>
                  </div>
                )}
                <ul className="mt-4 space-y-3">
                  {detail.data.orders.map((o) => (
                    <li key={o.id} className="rounded-xl border border-line p-3 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold">#{String(o.number).padStart(4, '0')} <span className="font-normal text-ink-3">· {formatDateTime(o.createdAt, restaurant.timezone)}{o.customerName ? ` · ${o.customerName}` : ''}{o.source === 'STAFF' ? ' · equipe' : ''}</span></span>
                        <OrderStatusBadge status={o.status} />
                      </div>
                      <ul className="mt-2 space-y-1">
                        {o.items.map((i) => (
                          <li key={i.id} className={cx('flex items-start gap-2', i.status === 'CANCELLED' && 'text-ink-3 line-through')}>
                            <span className="min-w-0 flex-1">{i.quantity}× {i.productName}{i.options.length > 0 && <span className="text-xs text-ink-3"> ({i.options.map((x) => x.optionName).join(', ')})</span>}</span>
                            {i.unitPriceCents != null && <span className="tabular-nums">{formatBRL(i.unitPriceCents * i.quantity)}</span>}
                            {can('orders.cancel_item') && i.status !== 'CANCELLED' && (
                              <button aria-label={`Cancelar ${i.productName}`} title="Cancelar item" onClick={() => setCancelling({ kind: 'item', id: i.id, label: `${i.quantity}× ${i.productName}` })} className="rounded p-0.5 text-ink-3 hover:text-danger-600">
                                <XCircle className="size-4" />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                      {can('orders.cancel_item') && canCancelOrder(o.status) && (
                        <Button variant="ghost" size="sm" className="mt-2 text-danger-600" icon={<XCircle className="size-4" />} onClick={() => setCancelling({ kind: 'order', id: o.id, label: `pedido #${String(o.number).padStart(4, '0')}` })}>
                          Cancelar pedido
                        </Button>
                      )}
                    </li>
                  ))}
                  {detail.data.orders.length === 0 && <li className="text-sm text-ink-3">Nenhum pedido ainda.</li>}
                </ul>
                <CancelDialog target={cancelling} onClose={() => setCancelling(null)} onDone={refresh} />
              </>
            ) : null}
          </section>
        ) : (
          table.status === 'ACTIVE' &&
          can('sessions.open') && (
            <Button icon={<Plus className="size-4" />} onClick={() => run(() => post(`/api/staff/tables/${table.id}/sessions`), `Comanda aberta na mesa ${table.label}`)}>Abrir comanda</Button>
          )
        )}

        <section className="rounded-2xl border border-line p-4">
          <h3 className="font-semibold">QR Code da mesa</h3>
          <div className="mt-3 flex flex-col items-center gap-3 sm:flex-row sm:items-start">
            {qr && <img src={qr} alt={`QR Code da mesa ${table.label}`} className="size-40 rounded-xl border border-line" />}
            <div className="flex flex-col gap-2 text-sm">
              <a href={qr} download={`mesa-${table.label}.png`} className="inline-flex h-9 items-center gap-2 rounded-xl border border-line bg-white px-3 font-medium hover:bg-paper-2"><Download className="size-4" /> Baixar PNG</a>
              <Link to={staffPath(`/mesas/imprimir?ids=${table.id}`)} className="inline-flex h-9 items-center gap-2 rounded-xl border border-line bg-white px-3 font-medium hover:bg-paper-2"><Printer className="size-4" /> Imprimir</Link>
              {can('tables.manage') && (
                <button
                  onClick={() => confirm('Gerar um QR Code novo? O impresso atual deixa de funcionar.') && run(() => post(`/api/staff/tables/${table.id}/regenerate-qr`), 'Novo QR Code gerado. Reimprima o cartão da mesa.')}
                  className="inline-flex h-9 items-center gap-2 rounded-xl px-3 text-left font-medium text-ink-3 hover:bg-paper-2 hover:text-ink"
                >
                  <RefreshCw className="size-4" /> Gerar novo QR Code
                </button>
              )}
            </div>
          </div>
        </section>

        {can('tables.manage') && (
          <section className="space-y-3 rounded-2xl border border-line p-4">
            <h3 className="font-semibold">Ajustes</h3>
            {editing ? (
              <form
                className="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void run(() => patch(`/api/staff/tables/${table.id}`, { label }), 'Mesa renomeada').then(() => setEditing(false));
                }}
              >
                <Input aria-label="Nome da mesa" value={label} onChange={(e) => setLabel(e.target.value)} />
                <Button type="submit">Salvar</Button>
              </form>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Renomear</Button>
                <Button size="sm" variant="secondary" onClick={() => run(() => patch(`/api/staff/tables/${table.id}`, { status: table.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' }), table.status === 'ACTIVE' ? 'Mesa desativada' : 'Mesa reativada')}>
                  {table.status === 'ACTIVE' ? 'Desativar' : 'Reativar'}
                </Button>
                {!table.session && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 className="size-4" />}
                    onClick={() => confirm(`Remover a mesa ${table.label}? O histórico de comandas é mantido.`) && run(() => post(`/api/staff/tables/${table.id}/archive`), 'Mesa removida').then(onClose)}
                  >
                    Remover
                  </Button>
                )}
              </div>
            )}
          </section>
        )}
      </div>
      {closing && table.session && (
        <CloseSessionDialog session={table.session} canOverride={can('account.close.override')} canSeeValues={can('accounts.view')} onClose={() => setClosing(false)} onDone={refresh} />
      )}
    </Sheet>
  );
}

function CloseSessionDialog({ session, canOverride, canSeeValues, onClose, onDone }: { session: NonNullable<TableRow['session']>; canOverride: boolean; canSeeValues: boolean; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const hasBalance = session.dueCents > 0 || (!canSeeValues && session.orders > 0 && !session.settled);
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await post(`/api/staff/sessions/${session.id}/close`, { reason });
      toast.success('Comanda encerrada');
      onDone();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Encerrar comanda ${session.number}`}
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button>{(!hasBalance || canOverride) && <Button variant={hasBalance ? 'danger' : 'primary'} loading={busy} onClick={submit}>Encerrar</Button>}</>}
    >
      <div className="space-y-3 text-sm">
        {error && <Alert tone="danger">{error}</Alert>}
        {hasBalance ? (
          <>
            <Alert tone="warning" title="Conta com saldo em aberto">
              {canSeeValues && <>Faltam {formatBRL(session.dueCents)} de {formatBRL(session.consumedCents)}. </>}Registre o pagamento em “Contas e caixa”. Encerrar com saldo é uma exceção registrada na auditoria.
            </Alert>
            {canOverride ? (
              <Field label="Justificativa (obrigatória)">
                {(id) => <textarea id={id} className="min-h-20 w-full rounded-xl border border-line px-3 py-2" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: pago em dinheiro no caixa" />}
              </Field>
            ) : (
              <p className="text-ink-2">Você não tem permissão para encerrar comandas com consumo. Chame um administrador.</p>
            )}
          </>
        ) : (
          <p>{session.consumedCents > 0 ? 'A conta está quitada.' : 'A comanda não tem consumo.'} Ela será encerrada e os clientes precisarão escanear o QR Code de novo para pedir.</p>
        )}
      </div>
    </Dialog>
  );
}

/** Folha de impressão: cartões com QR Code, um por mesa. "Salvar como PDF" no diálogo de impressão gera o arquivo. */
export function TablePrint({ restaurant }: { restaurant: TenantRestaurant }) {
  const q = useTables();
  const ids = new URLSearchParams(location.search).get('ids')?.split(',');
  const [codes, setCodes] = useState<Record<string, string>>({});
  const tables = (q.data ?? []).filter((t) => t.status === 'ACTIVE' && (!ids || ids.includes(t.id)));
  useEffect(() => {
    void Promise.all(tables.map(async (t) => [t.id, await QRCode.toDataURL(t.qrUrl, { margin: 1, width: 600, errorCorrectionLevel: 'M' })] as const)).then((pairs) => setCodes(Object.fromEntries(pairs)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);
  if (q.isPending) return <Spinner />;
  const ready = tables.length > 0 && tables.every((t) => codes[t.id]);
  return (
    <div className="bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line p-4 print:hidden">
        <div>
          <Link to={staffPath('/mesas')} className="text-sm text-ink-3 hover:text-ink">← Mesas</Link>
          <p className="font-semibold">{tables.length} QR Code(s) para imprimir</p>
          <p className="text-xs text-ink-3">Dica: no diálogo de impressão, escolha “Salvar como PDF” para baixar o arquivo.</p>
        </div>
        <Button icon={<Printer className="size-4" />} disabled={!ready} onClick={() => window.print()}>Imprimir</Button>
      </div>
      <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 print:grid-cols-3 print:gap-3 print:p-0">
        {tables.map((t) => (
          <div key={t.id} className="flex break-inside-avoid flex-col items-center rounded-2xl border-2 border-ink/80 p-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-3">{restaurant.name}</p>
            <p className="mt-1 font-display text-3xl font-semibold">Mesa {t.label}</p>
            {codes[t.id] && <img src={codes[t.id]} alt="" className="mt-2 w-full max-w-[11rem]" />}
            <p className="mt-2 text-sm font-medium">Escaneie para ver o cardápio e pedir</p>
            <p className="mt-1 text-[10px] text-ink-3">Garlic</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TableBadge({ label }: { label: string }) {
  return <Badge tone="brand">Mesa {label}</Badge>;
}
